/** iter 43 — Mobile / Notary inbox.
 *  Surfaces only when the current user has `is_notary = true`.
 *  Lists proof_documents that are pending notary signature for me, with a
 *  one-tap inline signature-pad workflow. */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import MobileShell from "../MobileShell";

export default function MNotaryInbox() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const [signing, setSigning] = useState(null);   // proof being signed
  const sigRef = useRef(null);

  const load = async () => {
    try { setItems((await api.get("/proofs/notary-inbox")).data || []); }
    catch (e) { setErr(e?.response?.data?.detail || e.message); }
  };
  useEffect(() => { load(); }, []);

  const sign = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      alert("Please draw your notary signature first"); return;
    }
    const dataUrl = sigRef.current.getCanvas().toDataURL("image/png");
    const blob = await (await fetch(dataUrl)).blob();
    const file = new File([blob], "notary-signature.png", { type: "image/png" });
    const fd = new FormData(); fd.append("signature", file);
    try {
      await api.post(`/proofs/${signing.id}/notary-sign`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setSigning(null);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
  };

  return (
    <MobileShell title="Notary inbox" back="/m/account">
      {!user?.is_notary && (
        <div className="m-card" style={{ background: "#fef3c7" }}>
          You are not flagged as a notary. Ask your admin to enable
          <code> users.is_notary </code> for your account.
        </div>
      )}
      {err && <div className="m-card" style={{ background: "#fee2e2" }}>{String(err)}</div>}
      {items === null && <div className="m-empty">Loading…</div>}
      {items?.length === 0 && <div className="m-empty" data-testid="m-notary-empty">Inbox zero ✨</div>}
      {items?.map(p => (
        <div key={p.id} className="m-card" data-testid={`m-notary-row-${p.id}`}>
          <div className="m-card-title">Job #{p.job_number || "—"}</div>
          <div className="m-card-sub">Server: {p.server_email}</div>
          <div className="m-row" style={{ marginTop: 8 }}>
            <button type="button" className="m-btn"
              onClick={() => navigate(`/m/proof/${p.id}`)}
              data-testid={`m-notary-view-${p.id}`}
            >VIEW PROOF</button>
            <button type="button" className="m-btn m-btn-primary"
              onClick={() => setSigning(p)}
              data-testid={`m-notary-sign-${p.id}`}
            >SIGN</button>
          </div>
        </div>
      ))}

      {signing && (
        <div className="m-card" data-testid="m-notary-sign-pad">
          <div className="m-card-title">Sign as notary</div>
          <div style={{ border: "1px solid hsl(var(--border))", borderRadius: 8, background: "#fff" }}>
            <SignatureCanvas ref={sigRef}
              canvasProps={{ width: 320, height: 140, "data-testid": "m-notary-canvas" }}
              penColor="#000" />
          </div>
          <div className="m-row" style={{ marginTop: 8 }}>
            <button type="button" className="m-btn" onClick={() => sigRef.current?.clear()}>CLEAR</button>
            <button type="button" className="m-btn" onClick={() => setSigning(null)}>CANCEL</button>
            <button type="button" className="m-btn m-btn-primary"
              onClick={sign}
              data-testid="m-notary-confirm"
            >COMMIT</button>
          </div>
        </div>
      )}
    </MobileShell>
  );
}
